import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import { ReactComponent as CheckIcon } from 'assets/icons/circle_check_black.svg';
import maxillaryChart from 'assets/icons/periodontal_maxillary.png';
import mandibularChart from 'assets/icons/periodontal_mandibular.png';
import classNames from 'classnames';
import styles from 'styles/pages/chart/PeriodontalExaminationChart.module.scss';
import {
  PeriodontalExaminationChartFields,
  PeriodontalCase,
  PeriodontalCaseLabel,
} from 'constants/chart/periodontalExaminationChart';
import { TChartValues } from 'types/chart/periodontalExaminationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import useCanvas from 'hooks/chart/useCanvas';
import { Layer, Stage, Line, Image as KonvaImg } from 'react-konva';

type TProps = {
  tool: TTool;
  strokeWidth: number;
  strokeColor: string;
  strokeActive: boolean;
  maxLines: TLine[];
  setMaxLines: (lines: TLine[]) => void;
  mandLines: TLine[];
  setMandLines: (lines: TLine[]) => void;
  drawingLines: TLine[];
  setDrawingLines: (lines: TLine[]) => void;
  stageRef: any;
  mandibularStageRef: any;
  penLayerRef: any;
  maxillaryValues: TChartValues;
  setMaxillaryValues: React.Dispatch<React.SetStateAction<TChartValues>>;
  mandibularValues: TChartValues;
  setMandibularValues: React.Dispatch<React.SetStateAction<TChartValues>>;
  isRegister: boolean;
  isReadOnly: boolean;
};

const PeriodontalExaminationChart = ({
  tool,
  strokeWidth,
  strokeColor,
  strokeActive,
  maxLines,
  setMaxLines,
  mandLines,
  setMandLines,
  drawingLines,
  setDrawingLines,
  stageRef,
  mandibularStageRef,
  penLayerRef,
  maxillaryValues,
  setMaxillaryValues,
  mandibularValues,
  setMandibularValues,
  isRegister,
  isReadOnly,
}: TProps) => {
  const [activeCell, setActiveCell] = useState({
    id: '',
    location: '',
    index: 0,
  });
  const [inputBoard, setInputBoard] = useState({
    active: false,
    type: '',
    top: 0,
    left: 0,
  });

  const inputBoardRef = useRef<HTMLDivElement>(null);

  const { setValue, watch } = useFormContext();
  const { useImage } = useCanvas({
    tool,
    strokeWidth,
    strokeColor,
    lines: drawingLines,
    setLines: setDrawingLines,
  });

  const maxCanvas = useCanvas({
    tool,
    strokeWidth,
    strokeColor,
    lines: maxLines,
    setLines: (newLines) => {
      if (!isRegister || isReadOnly) return;

      const uniqueNewLines = newLines.filter(
        (line) => !drawingLines.some((existingLine) => existingLine === line)
      );
      setMaxLines(newLines);
      setDrawingLines([...drawingLines, ...uniqueNewLines]);
    },
  });

  const mandCanvas = useCanvas({
    tool,
    strokeWidth,
    strokeColor,
    lines: mandLines,
    setLines: (newLines) => {
      if (!isRegister || isReadOnly) return;
      const uniqueNewLines = newLines.filter(
        (line) => !drawingLines.some((existingLine) => existingLine === line)
      );
      setMandLines(newLines);
      setDrawingLines([...drawingLines, ...uniqueNewLines]);
    },
  });

  const [maxillaryChartImg] = useImage(maxillaryChart);
  const [mandibularChartImg] = useImage(mandibularChart);

  useEffect(() => {
    const handleScroll = () => {
      if (activeCell.id === '') return;
      const position = getPosition(activeCell.id, activeCell.index);

      if (position) {
        const { top, left, height } = position;

        setInputBoard((prevState) => ({
          active: prevState.active,
          type: prevState.type,
          top: top + height,
          left: left,
        }));
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [activeCell.id]);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (
        inputBoardRef.current &&
        !inputBoardRef.current.contains(e.target as Node)
      ) {
        closeInputBoard();
      }
    };
    window.addEventListener('mousedown', clickOutside);
    return () => window.removeEventListener('mousedown', clickOutside);
  }, [inputBoardRef]);

  const getPosition = (id: string, index: number) => {
    const el = document.getElementById(id)?.children[index + 1];
    if (!el) return;
    const { top, left, height } = el.getBoundingClientRect();

    return { top, left, height };
  };

  const setChartValue = (
    prevValues: TChartValues,
    type: string,
    newValue: string | undefined
  ) => {
    if (prevValues && newValue != undefined) {
      const values = prevValues[type as keyof TChartValues];
      const alreadyIncluded =
        type === 'bleeding'
          ? values.includes(newValue)
          : values.find((value) => {
              const valueParts = value.split('_');
              const newValueParts = newValue.split('_');

              return (
                valueParts.slice(0, -1).join('_') ===
                newValueParts.slice(0, -1).join('_')
              );
            });

      return {
        calculus:
          type !== 'calculus'
            ? [...prevValues.calculus]
            : alreadyIncluded
            ? values.includes(newValue)
              ? prevValues.calculus.filter((value) => value !== newValue)
              : [
                  ...prevValues.calculus.filter((value) => {
                    const valueParts = value.split('_');
                    const newValueParts = newValue.split('_');

                    return (
                      valueParts.slice(0, -1).join('_') !==
                      newValueParts.slice(0, -1).join('_')
                    );
                  }),
                  newValue,
                ]
            : [...prevValues.calculus, newValue],
        probing:
          type !== 'probing'
            ? [...prevValues.probing]
            : alreadyIncluded
            ? values.includes(newValue)
              ? prevValues.probing.filter((value) => value !== newValue)
              : [
                  ...prevValues.probing.filter((value) => {
                    const valueParts = value.split('_');
                    const newValueParts = newValue.split('_');

                    return (
                      valueParts.slice(0, -1).join('_') !==
                      newValueParts.slice(0, -1).join('_')
                    );
                  }),
                  newValue,
                ]
            : [...prevValues.probing, newValue],
        bleeding:
          type !== 'bleeding'
            ? [...prevValues.bleeding]
            : alreadyIncluded
            ? prevValues.bleeding.filter((value) => value !== newValue)
            : [...prevValues.bleeding, newValue],
      };
    } else {
      return prevValues;
    }
  };

  const closeInputBoard = () => {
    setInputBoard({ active: false, type: '', top: 0, left: 0 });
    setActiveCell({ id: '', location: '', index: 0 });
  };

  const handleInputBoard = (
    id: string,
    location: string,
    type: string,
    index: number,
    value?: string
  ) => {
    if (!isRegister || isReadOnly) return;

    const position = getPosition(id, index);

    if (position) {
      const { top, left, height } = position;

      setActiveCell({
        id: id,
        location: location,
        index: index,
      });
      if (type !== 'bleeding') {
        setInputBoard({
          active: true,
          type: type,
          top: top + height,
          left: left,
        });
      } else {
        if (id.includes('maxillary')) {
          setMaxillaryValues((prevValues) =>
            setChartValue(prevValues, 'bleeding', value)
          );
        } else {
          setMandibularValues((prevValues) =>
            setChartValue(prevValues, 'bleeding', value)
          );
        }
      }
    }
  };

  const handleCalculus = (value: string) => {
    const isMaxillary = activeCell.id.includes('maxillary');
    const location = isMaxillary ? 'maxillary' : 'mandibular';
    const setChartValues = isMaxillary
      ? setMaxillaryValues
      : setMandibularValues;

    setChartValues((prevValues) =>
      setChartValue(
        prevValues,
        'calculus',
        `${location}_${activeCell.location}_${activeCell.index}_${value}`
      )
    );
    closeInputBoard();
  };

  const handleProbing = (value: number) => {
    const isMaxillary = activeCell.id.includes('maxillary');
    const location = isMaxillary ? 'maxillary' : 'mandibular';
    const setChartValues = isMaxillary
      ? setMaxillaryValues
      : setMandibularValues;

    setChartValues((prevValues) =>
      setChartValue(
        prevValues,
        'probing',
        `${location}_${activeCell.location}_${activeCell.index}_${value}`
      )
    );
    closeInputBoard();
  };

  const getValue = (type: string, id: string) => {
    const idParts = id.split('_');
    const isMaxillary = idParts[0] === 'maxillary';
    const values = isMaxillary ? maxillaryValues : mandibularValues;

    const fullValue =
      values[type as keyof TChartValues].find((value) => {
        const valueParts = value.split('_');
        return valueParts.slice(0, -1).join('_') === id;
      }) || '';

    return [!!fullValue, fullValue.split('_').pop()];
  };

  return (
    <>
      {inputBoard.active && (
        <div
          ref={inputBoardRef}
          className={styles[inputBoard.type]}
          style={{ top: inputBoard.top, left: inputBoard.left }}
        >
          {inputBoard.type === 'calculus' && (
            <>
              <button onClick={() => handleCalculus('v')}>
                연상치석 + 연하치석
              </button>
              <button onClick={() => handleCalculus('a')}>연상치석</button>
              <button onClick={() => handleCalculus('b')}>연하치석</button>
            </>
          )}
          {inputBoard.type === 'probing' && (
            <>
              {Array.from({ length: 9 }).map((_, index) => (
                <button key={index} onClick={() => handleProbing(index + 1)}>
                  {index + 1}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      <div className={styles.text_container}>
        <div className="flex items_center">
          <span className={styles.circle}></span>
          <span className="mr24 fs16">Periodontal Case :</span>
          <div className="flex gap16">
            <FormRadioBtn
              name={PeriodontalExaminationChartFields.periodontalCase}
              labelText={PeriodontalCaseLabel[PeriodontalCase.simple]}
              value={PeriodontalCase.simple}
              readOnly={!isRegister || isReadOnly}
            />
            <FormRadioBtn
              name={PeriodontalExaminationChartFields.periodontalCase}
              labelText={PeriodontalCaseLabel[PeriodontalCase.complex]}
              value={PeriodontalCase.complex}
              readOnly={!isRegister || isReadOnly}
            />
          </div>
        </div>
        <div className="flex gap24 fs12">
          <div>
            <div className="mt16 flex items_center">
              <CheckIcon width={16} height={16} />
              <span className="ml8">
                연상치석 : 해당 칸에 파란색 펜으로 &#39;O&#39; 표시
              </span>
            </div>
            <div className="mt10 flex items_center">
              <CheckIcon width={16} height={16} />
              <span className="ml8">
                치은출혈 : probing 후 약 30초 후 관찰하여 판단 ( 빨간색 펜으로
                &#39;O&#39; 표시 )
              </span>
            </div>
          </div>
          <div>
            <div className="mt16 flex items_center">
              <CheckIcon width={16} height={16} />
              <span className="ml8">
                연하치석 : 해당 칸에 빨간색 펜으로 &#39;・&#39; 표기
              </span>
            </div>
            <div className="mt10 flex items_center">
              <CheckIcon width={16} height={16} />
              <span className="ml8">GML : 녹색펜</span>
            </div>
          </div>
          <div className="flex items_end">
            <div className="flex items_center">
              <CheckIcon width={16} height={16} />
              <span className="ml8">PDL : 빨간펜</span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.title_wrapper}>
        <span className={styles.rectangle}></span>
        <span className={styles.title}>Maxillary</span>
      </div>
      <table className="w100">
        <colgroup>
          <col style={{ width: '103px' }} />
        </colgroup>
        <tbody>
          <tr id="maxillary1">
            <td className={styles.td_top_left}>Calculus</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_top_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'maxillary1' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('maxillary1', 'buccal', 'calculus', index)
                }
              >
                {getValue('calculus', `maxillary_buccal_${index}`) &&
                  getValue('calculus', `maxillary_buccal_${index}`)[0] && (
                    <i
                      className={classNames(
                        styles.calculus_icon,
                        styles[
                          `${
                            getValue(
                              'calculus',
                              `maxillary_buccal_${index}`
                            )[1] || ''
                          }`
                        ]
                      )}
                    ></i>
                  )}
              </td>
            ))}
          </tr>
          <tr id="maxillary2">
            <td className={styles.td_title}>Probing</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_center_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'maxillary2' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('maxillary2', 'buccal', 'probing', index)
                }
              >
                {getValue('probing', `maxillary_buccal_${index}`) &&
                  getValue('probing', `maxillary_buccal_${index}`)[0] &&
                  (getValue('probing', `maxillary_buccal_${index}`)[1] || '')}
              </td>
            ))}
          </tr>
          <tr id="maxillary3">
            <td className={styles.td_bottom_left}>Bleeding</td>
            {Array.from({ length: 16 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 15 ? styles.td_bottom_right : styles.td_bottom,
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                colSpan={3}
                onClick={() =>
                  handleInputBoard(
                    'maxillary3',
                    'buccal',
                    'bleeding',
                    index,
                    `buccal_${index < 8 ? 18 - index : index + 13}`
                  )
                }
              >
                <p
                  className={
                    maxillaryValues.bleeding.find(
                      (value) =>
                        value ===
                        `buccal_${index < 8 ? 18 - index : index + 13}`
                    )
                      ? styles.active
                      : ''
                  }
                >
                  {index < 8 ? 18 - index : index + 13}
                </p>
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className={styles.chart_wrapper}>
        <div className="flex flex_column justify_around fw700">
          <p>Buccal</p>
          <p>Lingual</p>
        </div>
        <Stage
          ref={stageRef}
          width={800}
          height={312}
          className="chart_image_area1"
          onTouchStart={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            maxCanvas.handleTouchStart(e);
          }}
          onTouchMove={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            maxCanvas.handleTouchMove(e);
          }}
          onTouchEnd={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            maxCanvas.handleTouchEnd(e);
          }}
        >
          <Layer>
            {maxillaryChartImg && (
              <KonvaImg image={maxillaryChartImg} width={800} height={312} />
            )}
          </Layer>
          <Layer ref={penLayerRef}>
            {maxLines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.strokeColor}
                strokeWidth={line.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  line.tool === 'eraser' ? 'destination-out' : 'source-over'
                }
              />
            ))}
          </Layer>
        </Stage>
      </div>

      <table className="w100">
        <colgroup>
          <col style={{ width: '103px' }} />
        </colgroup>
        <tbody>
          <tr id="maxillary4">
            <td className={styles.td_top_left}>Bleeding</td>
            {Array.from({ length: 16 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 15 ? styles.td_top_right_bold : styles.td_top,
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                colSpan={3}
                onClick={() =>
                  handleInputBoard(
                    'maxillary4',
                    'lingual',
                    'bleeding',
                    index,
                    `lingual_${index < 8 ? 18 - index : index + 13}`
                  )
                }
              >
                <p
                  className={
                    maxillaryValues.bleeding.find(
                      (value) =>
                        value ===
                        `lingual_${index < 8 ? 18 - index : index + 13}`
                    )
                      ? styles.active
                      : ''
                  }
                >
                  {index < 8 ? 18 - index : index + 13}
                </p>
              </td>
            ))}
          </tr>
          <tr id="maxillary5">
            <td className={styles.td_title}>Probing</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_center_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'maxillary5' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('maxillary5', 'lingual', 'probing', index)
                }
              >
                {getValue('probing', `maxillary_lingual_${index}`) &&
                  getValue('probing', `maxillary_lingual_${index}`)[0] &&
                  (getValue('probing', `maxillary_lingual_${index}`)[1] || '')}
              </td>
            ))}
          </tr>
          <tr id="maxillary6">
            <td className={styles.td_bottom_left}>Calculus</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_bottom_right : styles.td_bottom,
                  {
                    [styles.active]:
                      activeCell.id === 'maxillary6' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('maxillary6', 'lingual', 'calculus', index)
                }
              >
                {getValue('calculus', `maxillary_lingual_${index}`) &&
                  getValue('calculus', `maxillary_lingual_${index}`)[0] && (
                    <i
                      className={classNames(
                        styles.calculus_icon,
                        styles[
                          `${
                            getValue(
                              'calculus',
                              `maxillary_lingual_${index}`
                            )[1] || ''
                          }`
                        ]
                      )}
                    ></i>
                  )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className={styles.title_wrapper_margin}>
        <span className={styles.rectangle}></span>
        <span className={styles.title}>Mandibular</span>
      </div>
      <table className="w100">
        <colgroup>
          <col style={{ width: '103px' }} />
        </colgroup>
        <tbody>
          <tr id="mandibular1">
            <td className={styles.td_top_left}>Bleeding</td>
            {Array.from({ length: 16 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 15 ? styles.td_top_right_bold : styles.td_top,
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                colSpan={3}
                onClick={() =>
                  handleInputBoard(
                    'mandibular1',
                    'lingual',
                    'bleeding',
                    index,
                    `lingual_${index < 8 ? 48 - index : index + 23}`
                  )
                }
              >
                <p
                  className={
                    mandibularValues.bleeding.find(
                      (value) =>
                        value ===
                        `lingual_${index < 8 ? 48 - index : index + 23}`
                    )
                      ? styles.active
                      : ''
                  }
                >
                  {index < 8 ? 48 - index : index + 23}
                </p>
              </td>
            ))}
          </tr>
          <tr id="mandibular2">
            <td className={styles.td_title}>Probing</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_center_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'mandibular2' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('mandibular2', 'lingual', 'probing', index)
                }
              >
                {getValue('probing', `mandibular_lingual_${index}`) &&
                  getValue('probing', `mandibular_lingual_${index}`)[0] &&
                  (getValue('probing', `mandibular_lingual_${index}`)[1] || '')}
              </td>
            ))}
          </tr>
          <tr id="mandibular3">
            <td className={styles.td_bottom_left}>Calculus</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_bottom_right : styles.td_bottom,
                  {
                    [styles.active]:
                      activeCell.id === 'mandibular3' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('mandibular3', 'lingual', 'calculus', index)
                }
              >
                {getValue('calculus', `mandibular_lingual_${index}`) &&
                  getValue('calculus', `mandibular_lingual_${index}`)[0] && (
                    <i
                      className={classNames(
                        styles.calculus_icon,
                        styles[
                          `${
                            getValue(
                              'calculus',
                              `mandibular_lingual_${index}`
                            )[1] || ''
                          }`
                        ]
                      )}
                    ></i>
                  )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className={styles.chart_wrapper}>
        <div className="flex flex_column justify_around fw700">
          <p>Lingual</p>
          <p>Buccal</p>
        </div>
        <Stage
          ref={mandibularStageRef}
          width={800}
          height={312}
          className="chart_image_area2"
          onTouchStart={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            mandCanvas.handleTouchStart(e);
          }}
          onTouchMove={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            mandCanvas.handleTouchMove(e);
          }}
          onTouchEnd={(e: any) => {
            if (!isRegister || isReadOnly || !strokeActive) return;
            mandCanvas.handleTouchEnd(e);
          }}
        >
          <Layer>
            {mandibularChartImg && (
              <KonvaImg image={mandibularChartImg} width={800} height={312} />
            )}
          </Layer>
          <Layer ref={penLayerRef}>
            {mandLines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.strokeColor}
                strokeWidth={line.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  line.tool === 'eraser' ? 'destination-out' : 'source-over'
                }
              />
            ))}
          </Layer>
        </Stage>
      </div>

      <table className="w100">
        <colgroup>
          <col style={{ width: '103px' }} />
        </colgroup>
        <tbody>
          <tr id="mandibular4">
            <td className={styles.td_top_left}>Calculus</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_top_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'mandibular4' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('mandibular4', 'baccal', 'calculus', index)
                }
              >
                {getValue('calculus', `mandibular_baccal_${index}`) &&
                  getValue('calculus', `mandibular_baccal_${index}`)[0] && (
                    <i
                      className={classNames(
                        styles.calculus_icon,
                        styles[
                          `${
                            getValue(
                              'calculus',
                              `mandibular_baccal_${index}`
                            )[1] || ''
                          }`
                        ]
                      )}
                    ></i>
                  )}
              </td>
            ))}
          </tr>
          <tr id="mandibular5">
            <td className={styles.td_title}>Probing</td>
            {Array.from({ length: 48 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 47 ? styles.td_center_right : styles.td,
                  {
                    [styles.active]:
                      activeCell.id === 'mandibular5' &&
                      activeCell.index === index,
                  },
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                onClick={() =>
                  handleInputBoard('mandibular5', 'buccal', 'probing', index)
                }
              >
                {getValue('probing', `mandibular_buccal_${index}`) &&
                  getValue('probing', `mandibular_buccal_${index}`)[0] &&
                  (getValue('probing', `mandibular_buccal_${index}`)[1] || '')}
              </td>
            ))}
          </tr>
          <tr id="mandibular6">
            <td className={styles.td_bottom_left}>Bleeding</td>
            {Array.from({ length: 16 }).map((_, index) => (
              <td
                key={index}
                className={classNames(
                  index === 15 ? styles.td_bottom_right : styles.td_bottom,
                  { [styles.pointer]: isRegister && !isReadOnly }
                )}
                colSpan={3}
                onClick={() =>
                  handleInputBoard(
                    'mandibular6',
                    'buccal',
                    'bleeding',
                    index,
                    `buccal_${index < 8 ? 48 - index : index + 23}`
                  )
                }
              >
                <p
                  className={
                    mandibularValues.bleeding.find(
                      (value) =>
                        value ===
                        `buccal_${index < 8 ? 48 - index : index + 23}`
                    )
                      ? styles.active
                      : ''
                  }
                >
                  {index < 8 ? 48 - index : index + 23}
                </p>
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className={styles.text_container}>
        <div className="flex items_center">
          <span className={styles.circle}></span>
          <span className="mr24 fs16">Calculus rate : </span>
          <div className="flex gap16 items_center">
            <input
              type="text"
              value={
                maxillaryValues.calculus.length +
                  mandibularValues.calculus.length >
                0
                  ? maxillaryValues.calculus.length +
                    mandibularValues.calculus.length
                  : ''
              }
              placeholder="치석이 있는 치면 수"
              className={styles.input}
              readOnly
            />
            <span className="fs16">&#47;</span>
            <input
              type="number"
              value={
                watch(PeriodontalExaminationChartFields.calculusCheckIndex) ||
                ''
              }
              placeholder="검사 치면수"
              className={styles.input}
              readOnly={!isRegister || isReadOnly}
              onChange={(e) =>
                setValue(
                  PeriodontalExaminationChartFields.calculusCheckIndex,
                  Number(e.target.value)
                )
              }
            />
            <span className="fs16">X 100</span>
            <span className="fs16">=</span>
            <input
              type="text"
              value={
                watch(PeriodontalExaminationChartFields.calculusCheckIndex)
                  ? (
                      ((maxillaryValues.calculus.length +
                        mandibularValues.calculus.length) /
                        (watch(
                          PeriodontalExaminationChartFields.calculusCheckIndex
                        ) || 0)) *
                      100
                    ).toFixed(1)
                  : 0
              }
              className={styles.rate_input}
              readOnly
            />
            <span className="fs16">%</span>
          </div>
        </div>
        <div className="flex items_center mt10">
          <span className="fs16 mr8">※</span>
          <span className="fs12">
            치석률 : 치석이 있는 치면 수 / 검사 치면수 * 100
          </span>
        </div>
        <div className={styles.divider_line}></div>
        <div className="flex items_center">
          <span className={styles.circle}></span>
          <span className="mr24 fs16">Bleeding rate : </span>
          <div className="flex gap16 items_center">
            <input
              type="text"
              value={
                maxillaryValues.bleeding.length +
                  mandibularValues.bleeding.length >
                0
                  ? maxillaryValues.bleeding.length +
                    mandibularValues.bleeding.length
                  : ''
              }
              placeholder="출혈이 있는 치면 수"
              className={styles.input}
              readOnly
            />
            <span className="fs16">&#47;</span>
            <input
              type="number"
              value={
                watch(PeriodontalExaminationChartFields.bleedingCheckIndex) ||
                ''
              }
              placeholder="검사 치면수"
              className={styles.input}
              readOnly={!isRegister || isReadOnly}
              onChange={(e) =>
                setValue(
                  PeriodontalExaminationChartFields.bleedingCheckIndex,
                  Number(e.target.value)
                )
              }
            />
            <span className="fs16">X 100</span>
            <span className="fs16">=</span>
            <input
              type="text"
              value={
                watch(PeriodontalExaminationChartFields.bleedingCheckIndex)
                  ? (
                      ((maxillaryValues.bleeding.length +
                        mandibularValues.bleeding.length) /
                        (watch(
                          PeriodontalExaminationChartFields.bleedingCheckIndex
                        ) || 0)) *
                      100
                    ).toFixed(1)
                  : 0
              }
              className={styles.rate_input}
              readOnly
            />
            <span className="fs16">%</span>
          </div>
        </div>
        <div className="flex items_center mt10">
          <span className="fs16 mr8">※</span>
          <span className="fs12">
            치은 출혈률 : 출혈이 있는 치면 수 / 검사 치면수 * 100
          </span>
        </div>
      </div>
    </>
  );
};

export default PeriodontalExaminationChart;
